<script setup lang="ts">
/**
 * Живой WebGL-фон для drawer-панели навигации.
 * Органический FBM-noise с реакцией на мышь.
 * Чистый WebGL — без Three.js, минимальный вес.
 */

const canvasRef = ref<HTMLCanvasElement | null>(null);

const props = defineProps<{
  active: boolean;
}>();

const { enableDrawerBg, dprCap, tier } = use3DQuality();

// --- WebGL setup ---
const vertexShaderSource = `
  attribute vec2 a_position;
  varying vec2 vUv;
  void main() {
    vUv = a_position * 0.5 + 0.5;
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`;

const fragmentShaderSource = `
  precision mediump float;

  varying vec2 vUv;
  uniform float uTime;
  uniform vec2 uMouse;
  uniform vec2 uResolution;

  // Gradient noise hash — reused across octaves
  vec3 hash3(vec2 p) {
    vec3 q = vec3(
      dot(p, vec2(127.1, 311.7)),
      dot(p, vec2(269.5, 183.3)),
      dot(p, vec2(419.2, 371.9))
    );
    return fract(sin(q) * 43758.5453) * 2.0 - 1.0;
  }

  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    float n = mix(
      mix(dot(hash3(i).xy, f),
          dot(hash3(i + vec2(1.0, 0.0)).xy, f - vec2(1.0, 0.0)), u.x),
      mix(dot(hash3(i + vec2(0.0, 1.0)).xy, f - vec2(0.0, 1.0)),
          dot(hash3(i + vec2(1.0, 1.0)).xy, f - vec2(1.0, 1.0)), u.x),
      u.y
    );
    return 0.5 + 0.5 * n;
  }

  // Full-detail FBM (4 octaves — main visual output)
  float fbm(vec2 p) {
    float value = 0.0;
    float amp = 0.5;
    float freq = 1.0;
    for (int i = 0; i < 4; i++) {
      value += amp * noise(p * freq);
      amp *= 0.5;
      freq *= 2.0;
    }
    return value;
  }

  // Cheap FBM for intermediate warp layers (3 octaves — detail not visible)
  float fbmWarp(vec2 p) {
    float value = 0.0;
    float amp = 0.5;
    float freq = 1.0;
    for (int i = 0; i < 3; i++) {
      value += amp * noise(p * freq);
      amp *= 0.5;
      freq *= 2.0;
    }
    return value;
  }

  void main() {
    vec2 uv = vUv;
    float aspect = uResolution.x / uResolution.y;
    vec2 st = vec2(uv.x * aspect, uv.y);

    float t = uTime * 0.15;

    vec2 mouse = vec2(uMouse.x * aspect, uMouse.y);
    float mouseDist = length(st - mouse);

    // Cursor warp — smooth falloff
    vec2 warpDir = normalize(st - mouse + 0.001);
    float warpStrength = smoothstep(0.25, 0.0, mouseDist) * 0.08;
    vec2 warpedSt = st + warpDir * warpStrength;

    // Domain Warping (Inigo Quilez technique)
    // Warp layers use cheaper fbmWarp (3 octaves); only final output uses full fbm (4)
    // Ускоренная деформация — "бензиновые пятна"
    float t2 = uTime * 0.2;

    vec2 q = vec2(
      fbmWarp(warpedSt * 0.8 + vec2(t2 * 0.3, t2 * 0.5)),
      fbmWarp(warpedSt * 0.8 + vec2(-t2 * 0.4, t2 * 0.2) + 5.2)
    );

    vec2 r = vec2(
      fbmWarp(warpedSt * 0.8 + 4.0 * q + vec2(t2 * 0.2, t2 * 0.1) + 1.7),
      fbmWarp(warpedSt * 0.8 + 4.0 * q + vec2(-t2 * 0.3, t2 * 0.4) + 8.3)
    );

    float combined = fbm(warpedSt * 0.8 + 3.5 * r);

    // Усиление FBM вокруг курсора — концентрация "кругов"
    float cursorBoost = smoothstep(0.8, 0.0, mouseDist);
    // Мелкие детали рядом с курсором
    float detailFbm = fbmWarp(warpedSt * 2.5 + 3.5 * r + uTime * 0.05);
    combined = mix(combined, combined + detailFbm * 0.7, cursorBoost);
    // Усиление яркости у курсора
    combined *= (1.0 + cursorBoost * 1.2);

    vec3 darkPage = vec3(0.06);
    vec3 dark     = vec3(0.13);
    vec3 hiColor  = vec3(0.12, 0.09, 0.18); // лёгкий фиолетовый отсвет

    vec3 baseColor = mix(darkPage, dark, (1.0 - combined) * 0.8 + 0.1);
    // Лёгкий фиолетовый оттенок в ярких зонах FBM
    baseColor += hiColor * combined * cursorBoost;

    // Бензиновые переливы — радужные оттенки от "толщины плёнки"
    float film = fbmWarp(warpedSt * 1.2 + vec2(uTime * 0.1, -uTime * 0.07));
    vec3 oilSheen = 0.15 * vec3(
      0.5 + 0.5 * sin(film * 6.28),          // R
      0.5 + 0.5 * sin(film * 6.28 + 2.09),   // G (сдвиг 120°)
      0.5 + 0.5 * sin(film * 6.28 + 4.19)    // B (сдвиг 240°)
    );
    baseColor += oilSheen * smoothstep(0.4, 0.0, mouseDist);

    // Vignette
    float vignette = smoothstep(0.0, 0.7, length(uv - 0.5));
    baseColor *= 1.0 - vignette * 0.25;

    gl_FragColor = vec4(baseColor, 1.0);
  }
`;

let gl: WebGLRenderingContext | null = null;
let program: WebGLProgram | null = null;
let rafId: number | null = null;
let startTime: number = 0;

// Uniform locations
let uTimeLoc: WebGLUniformLocation | null = null;
let uMouseLoc: WebGLUniformLocation | null = null;
let uResolutionLoc: WebGLUniformLocation | null = null;

// Храним позицию мыши в viewport-координатах (абсолютные пиксели экрана)
const mouseViewport = { x: 0, y: 0 };
const mouseCurrent = { x: 0.5, y: 0.5 };
let isFirstFrame = true;

const compileShader = (
  source: string,
  type: number,
): WebGLShader | null => {
  if (!gl) return null;
  const shader = gl.createShader(type);
  if (!shader) return null;

  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.warn('Shader compile error:', gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }
  return shader;
};

const initWebGL = () => {
  const canvas = canvasRef.value;
  if (!canvas) return;

  gl = canvas.getContext('webgl', {
    alpha: false,
    antialias: false,
    premultipliedAlpha: false,
  });
  if (!gl) return;

  const vertexShader = compileShader(vertexShaderSource, gl.VERTEX_SHADER);
  const fragmentShader = compileShader(fragmentShaderSource, gl.FRAGMENT_SHADER);
  if (!vertexShader || !fragmentShader) return;

  program = gl.createProgram();
  if (!program) return;

  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.warn('Program link error:', gl.getProgramInfoLog(program));
    return;
  }

  gl.useProgram(program);

  // Fullscreen quad
  const buffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
    gl.STATIC_DRAW,
  );

  const posLoc = gl.getAttribLocation(program, 'a_position');
  gl.enableVertexAttribArray(posLoc);
  gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

  // Uniforms
  uTimeLoc = gl.getUniformLocation(program, 'uTime');
  uMouseLoc = gl.getUniformLocation(program, 'uMouse');
  uResolutionLoc = gl.getUniformLocation(program, 'uResolution');

  startTime = performance.now();
  resizeCanvas();
};

const resizeCanvas = () => {
  const canvas = canvasRef.value;
  if (!canvas || !gl) return;

  // Адаптивный DPR: на low — 0.25, medium — 0.35, high — 0.5
  const multiplier = tier.value === 'low' ? 0.25 : tier.value === 'medium' ? 0.35 : 0.5;
  const dpr = Math.min(window.devicePixelRatio, dprCap.value) * multiplier;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  gl.viewport(0, 0, canvas.width, canvas.height);
};

const render = () => {
  if (!gl || !program) return;

  // Считаем координаты мыши относительно drawer-а (с учётом его анимации)
  const drawerEl = canvasRef.value?.parentElement;
  let targetX = 0.5;
  let targetY = 0.5;
  if (drawerEl) {
    const rect = drawerEl.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      targetX = (mouseViewport.x - rect.left) / rect.width;
      targetY = 1.0 - (mouseViewport.y - rect.top) / rect.height;
    }
  }

  // Первый кадр после открытия — мгновенно в позицию курсора
  if (isFirstFrame) {
    mouseCurrent.x = targetX;
    mouseCurrent.y = targetY;
    isFirstFrame = false;
    // Плавно проявляем canvas
    requestAnimationFrame(() => {
      canvasRef.value?.classList.add('drawer-canvas-bg--visible');
    });
  } else {
    mouseCurrent.x += (targetX - mouseCurrent.x) * 0.12;
    mouseCurrent.y += (targetY - mouseCurrent.y) * 0.12;
  }

  const elapsed = (performance.now() - startTime) / 1000;

  gl.uniform1f(uTimeLoc, elapsed);
  gl.uniform2f(uMouseLoc, mouseCurrent.x, mouseCurrent.y);
  gl.uniform2f(
    uResolutionLoc,
    canvasRef.value!.width,
    canvasRef.value!.height,
  );
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

  rafId = requestAnimationFrame(render);
};

const startRender = () => {
  if (rafId !== null) return;
  rafId = requestAnimationFrame(render);
};

const stopRender = () => {
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
};

const onMouseMove = (event: MouseEvent) => {
  // Храним абсолютные координаты экрана — пересчёт в render
  mouseViewport.x = event.clientX;
  mouseViewport.y = event.clientY;
};

const onTouchMove = (event: TouchEvent) => {
  const touch = event.touches[0];
  if (!touch) return;
  mouseViewport.x = touch.clientX;
  mouseViewport.y = touch.clientY;
};

// Реагируем на prop active — запускаем/останавливаем рендер
watch(
  () => props.active,
  (isActive) => {
    if (isActive && enableDrawerBg.value) {
      isFirstFrame = true;
      // Запускаем рендер сразу, canvas проявится через opacity transition
      resizeCanvas();
      startRender();
    } else {
      stopRender();
      canvasRef.value?.classList.remove('drawer-canvas-bg--visible');
    }
  },
);

onMounted(() => {
  // На low-tier FBM-шейдер отключён — drawer работает без WebGL-фона
  if (!enableDrawerBg.value) return;

  initWebGL();
  if (props.active) {
    startRender();
  }
  window.addEventListener('resize', resizeCanvas);
  // Слушаем глобально — чтобы знать позицию курсора ещё до открытия drawer
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('touchmove', onTouchMove, { passive: true });

  // Пауза RAF при скрытой вкладке — экономия батареи
  document.addEventListener('visibilitychange', onVisibilityChange);
});

// Пауза/возобновление рендера при переключении вкладок
const onVisibilityChange = () => {
  if (document.hidden) {
    stopRender();
  } else if (props.active && enableDrawerBg.value) {
    startRender();
  }
};

onUnmounted(() => {
  stopRender();
  window.removeEventListener('resize', resizeCanvas);
  document.removeEventListener('mousemove', onMouseMove);
  document.removeEventListener('touchmove', onTouchMove);
  document.removeEventListener('visibilitychange', onVisibilityChange);

  // Очистка WebGL
  if (program && gl) {
    gl.deleteProgram(program);
  }
  gl = null;
  program = null;
});
</script>

<template lang="pug">
canvas.drawer-canvas-bg(
  ref="canvasRef"
  aria-hidden="true"
)
</template>

<style lang="sass" scoped>
.drawer-canvas-bg
  position: absolute
  top: 0
  left: 0
  width: 100%
  height: 100%
  display: block
  pointer-events: none
  opacity: 0
  transition: opacity 1s ease-in

.drawer-canvas-bg--visible
  opacity: 1
</style>
